import { FC, useState, useContext } from 'react';
import { TileContent } from '../Tile';
import { SideOldCockpitSideTiles, TilesInterface } from './tiles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

import * as S from './styles';
import {
  openDrawer,
  handleDragEnd,
  useHandleTutorialLogic
} from './gridHelper';
import { DrawerWrapper } from '../DrawerWrapper';
import { TilesContext } from '../../handlers/TilesHandler';
import { TILES } from '../DrawerWrapper/tileSummaries';
import { useMatomo } from '@datapunt/matomo-tracker-react';

export const Grid: FC = () => {
  const [visibleDrawer, setVisibleDrawer] = useState<boolean>(false);
  const [currentTileKey, setCurrentTileKey] = useState<string>();
  const { mainTiles, setMainTiles } = useContext(TilesContext);
  const [draggedTile, setDraggedTile] = useState<string>();
  const [draggedOverTile, setDraggedOverTile] = useState<string>();
  const [mountDrawer, setMountDrawer] = useState<boolean>(true);

  useHandleTutorialLogic(setMountDrawer, setVisibleDrawer);
  const { isInSpecialMode } = useContext(TilesContext);
  const { trackEvent } = useMatomo();

  const renderTiles = (tilesToRender: Array<TilesInterface>) =>
    tilesToRender?.map(({ key, name, isStatic, height, width, padding }) => {
      const appliedWidth = (width?: string) => {
        if (width) {
          return width;
        }
        return isInSpecialMode ? 'calc(33.333% - 5px)' : 'calc(25% - 5px)';
      };

      const prepareTileName = (name: string) => {
        // Append "-special-mode" to all tiles when in special mode, so that the wider image is used.
        // Exclude the default tiles Pulse, Logo and Time since they are not image based
        if (
          isInSpecialMode &&
          !['pulse', 'logo', 'real-date-time'].includes(name)
        ) {
          return name + '-special-mode';
        } else {
          return name;
        }
      };

      return (
        <S.GridItemWrapper
          key={key}
          height={height}
          padding={padding}
          width={appliedWidth(width)}
        >
          {name ? (
            <S.Tile
              data-tour={`tile-${key}`}
              isDraggedOver={draggedOverTile === name}
              className={isStatic ? 'static-tile' : ''}
              isLogoTile={name === 'logo'}
              draggable={!isStatic}
              onDragStart={e => {
                if (isStatic) {
                  e.preventDefault();
                  return false;
                } else {
                  setDraggedTile(name);
                }
              }}
              onDragOver={e => {
                if (isStatic) {
                  e.preventDefault();
                  return false;
                } else {
                  setDraggedOverTile(name);
                }
              }}
              onDragEnd={() => {
                setDraggedOverTile('');
                handleDragEnd(
                  draggedTile,
                  draggedOverTile,
                  setMainTiles,
                  trackEvent
                );
              }}
            >
              <TileContent name={prepareTileName(name)} />
              {!isStatic &&
                !TILES.some(spt => spt.autofillTile && spt.name === name) && (
                  <EditIcon
                    className="grid-edit-icon"
                    onClick={e =>
                      openDrawer(e, key, setCurrentTileKey, setVisibleDrawer)
                    }
                  />
                )}
            </S.Tile>
          ) : (
            <S.AddTileButton
              data-testid="add-tile"
              data-tour={`tile-${key}`}
              className="tile-button"
              onClick={e => {
                openDrawer(e, key, setCurrentTileKey, setVisibleDrawer);
              }}
            >
              <AddIcon sx={{ fontSize: 90 }} />
            </S.AddTileButton>
          )}
        </S.GridItemWrapper>
      );
    });
  return (
    <>
      <S.SideTiles>{renderTiles(SideOldCockpitSideTiles)}</S.SideTiles>
      <S.MainTiles data-tour={'main-grid'}>
        {renderTiles(mainTiles)}
      </S.MainTiles>
      <DrawerWrapper
        currentTileKey={currentTileKey}
        setTiles={setMainTiles}
        setVisibleDrawer={setVisibleDrawer}
        tiles={mainTiles}
        visibleDrawer={visibleDrawer}
        mountDrawer={mountDrawer}
      />
    </>
  );
};
